import { Fragment, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';

import {
  actionLabel,
  blockedReasonLabel,
  isDestructive,
  type BlockedContext,
} from '../action-presentation';
import type { FlowAction, FlowActionKind } from '../types';

interface Props {
  actions: FlowAction[];
  onSelect: (action: FlowAction) => void;
  /** G2: rol de la etapa y quién falta, para explicar el bloqueo ("Solo los revisores…"). */
  context?: Pick<BlockedContext, 'role' | 'pending'>;
  /** Operación en curso: deshabilita todo para evitar doble envío (UX-FB-01). */
  pending?: boolean;
}

/** Estilo del botón por tipo de acción (coherencia visual, UX-COMP-01). */
function variantOf(action: FlowActionKind): string {
  if (isDestructive(action)) {
    return 'danger';
  }
  if (action === 'approve' || action === 'transition') {
    return '';
  }
  return 'mut';
}

/** Acciones de la etapa actual. Renderiza `availableActions` del servidor: cada una
 *  habilitada o deshabilitada con su razón; el click delega en el contenedor. */
export function StageActions({
  actions,
  onSelect,
  pending = false,
  context = {},
}: Props): ReactElement {
  if (actions.length === 0) {
    return (
      <div className="card rise d3">
        <div className="cb">
          <p className="soft" style={{ fontSize: 12 }}>
            No hay acciones disponibles en esta etapa.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="card rise d3">
      <div className="cb" style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
        {actions.map((a) => {
          const reason = a.enabled
            ? ''
            : blockedReasonLabel(a.blockedReason, {
                ...context,
                action: a.action,
                toStatus: a.toStatus,
              });
          const key = `${a.action}:${a.toStatus ?? ''}`;
          const reasonId = `blocked-${key.replace(/[^a-z_-]/gi, '-')}`;
          // La razón del bloqueo se lee en pantalla (táctil incluido) y la anuncia el
          // lector de pantalla; el tooltip queda como refuerzo en escritorio.
          return (
            <Fragment key={key}>
              <button
                type="button"
                className={cn('btn', variantOf(a.action))}
                disabled={!a.enabled || pending}
                title={reason === '' ? undefined : reason}
                aria-describedby={reason === '' ? undefined : reasonId}
                onClick={() => onSelect(a)}
              >
                {actionLabel(a.action, a.toStatus)}
              </button>
              {reason !== '' && (
                <p id={reasonId} className="soft" style={{ fontSize: 11.5, margin: '-4px 0 0' }}>
                  {reason}
                </p>
              )}
            </Fragment>
          );
        })}
        {actions.some((a) => a.action === 'approve') && (
          <p className="soft" style={{ fontSize: 11.5 }}>
            En aprobación la versión no admite cambios: todos los aprobadores firman el mismo
            contenido.
          </p>
        )}
      </div>
    </div>
  );
}
